import { useEffect } from "preact/hooks";
import type { StepProps } from "../app";
import { ContactAdvisor } from "../ContactAdvisor";
import { approvedPrograms } from "@ks-dac/shared";
import { track } from "../lib/analytics";

/**
 * THE COMPLIANCE-SENSITIVE SCREEN.
 *
 * DAC designation is an INPUT to certain incentives. It is not, by itself, a
 * qualification for anything. The federal Low-Income Communities Bonus Credit
 * uses New Markets Tax Credit geography, not NYSERDA DACs, and NY-Sun's
 * affordable solar adder is income-qualified through submitted documentation,
 * not by tract.
 *
 * So this screen states a fact about a public dataset and promises a callback.
 * It names no program, quotes no figure, and uses no word that implies either.
 * `approvedPrograms()` returns an empty list until Nico's review lands, and the
 * test file enforces the vocabulary rather than trusting this comment.
 */
export function Step3Result({ state }: StepProps) {
  const verdict = state.verdict;

  useEffect(() => {
    if (!verdict) return;
    track("step_view", { step: "result", submission_id: state.submissionId });
    if (verdict.status === "resolved") {
      // county is coarse geography, not PII. No address, no ZIP.
      track("verdict_shown", {
        is_dac: verdict.tract.isDac,
        county: verdict.tract.county,
        submission_id: state.submissionId,
      });
    }
  }, [verdict]);

  if (!verdict) return null;

  if (verdict.status === "unresolved") {
    return (
      <section class="step">
        <h2>Your result</h2>
        <p class="notice" role="status">
          {verdict.message}
        </p>
        {/* "We have your details" is now literally true, and saying so is the
            point of this arm existing at all. Until 2026-07-30 a lookup miss
            ended the flow at step 1, so the promise of manual follow-up in
            `verdict.message` above had no contact details behind it. */}
        <p>
          We have your details, and a Kasselman Solar advisor will look into this and get back to
          you.
        </p>
        {/* This screen has no Back or Continue, so the row holds the advisor alone
            and `actions-center` centres it instead of leaving it hard left. It
            matters most here: an address we could not resolve is exactly when
            someone wants to talk to a person rather than wait for a callback. */}
        <div class="actions actions-center">
          <ContactAdvisor stepName="Result" submissionId={state.submissionId} />
        </div>
      </section>
    );
  }

  const { tract, vintage } = verdict;
  const programs = approvedPrograms();

  return (
    <section class="step">
      <h2>Your result</h2>

      {/* One class for both outcomes, deliberately. The earlier `is-dac` variant
          tinted the designated case green, and green reads as "success", which
          implies a benefit the customer may not receive, the exact implication
          this file's header comment and the copy gate exist to keep out. The
          panel is a neutral fact panel and the words carry the difference. */}
      <div class="verdict" role="status">
        {/* BOTH ARMS ARE REVIEWED COPY and change together on purpose, held to one
            voice: "You are / You are not located in", second person, same sentence
            shape. Editing one arm alone is how the two outcomes end up sounding like
            they came from different products. All three sentences are pinned in
            REVIEWED_COPY against docs/legal-review-2026-08-05.md (ADR-0001).

            Do not restore "which means" on the designated arm, and do not reattach
            "here" to "bigger". Both were removed on 2026-08-05: the first presents a
            program rule about an area as a promise about this property, and the
            second was false in PSEG Long Island territory. That territory is now
            unreachable, and the sentence was still reworded, because relying on a
            routing rule to keep a sentence true is how it becomes false again. */}
        {tract.isDac ? (
          <p>
            <strong>You are located in a Disadvantaged Community.</strong> New York State offers a
            higher battery storage incentive rate for qualifying projects in these areas.
          </p>
        ) : (
          <>
            <p>
              <strong>You are not located in a Disadvantaged Community.</strong> New York State
              does not designate the census tract covering this address as one.
            </p>
            {/* REVIEWED COPY, added 2026-08-05. The DAC tract is one of three
                routes to the higher rate, and this widget only checks that one,
                so a bare "no" reads as "you do not get it" to precisely the
                audience the income route exists for. Under GBL 350-a false
                advertising includes failing to reveal a material fact, and this
                omission is material. See docs/legal-review-2026-08-05.md,
                question 1. It is also the better-converting sentence, which is
                not why it is here. */}
            <p>
              Households below 80% of Area Median Income may still qualify for the higher
              incentive through income verification. Your advisor can check this with you.
            </p>
          </>
        )}
        <p class="place">
          {tract.cityTown}, {tract.county} County
        </p>
      </div>

      <p>
        A Kasselman Solar advisor will walk you through what this means for your project and
        answer your questions. We will be in touch shortly.
      </p>

      {/* Empty until PROGRAM_COPY_APPROVED flips AND real entries are written.
          Absent, not greyed out and not behind a tooltip. */}
      {programs.length > 0 && (
        <ul class="programs">
          {programs.map((p) => (
            <li key={p.id}>
              <strong>{p.name}</strong> {p.role} {p.remainingConditions}
            </li>
          ))}
        </ul>
      )}

      {/* REVIEWED COPY, added 2026-08-05. Does double duty. It is the qualifier
          Nico required on the designated arm (rates are NYSERDA's, vary by utility
          territory, and run on first-come block funding), and it is what keeps
          "higher" substantiated as the blocks step down: $450/kWh is Block 1 of a
          first-come structure, so the claim is true only while that block is
          funded at that rate. Rendered on BOTH arms, because the income-route
          sentence on the non-designated arm names the higher incentive too. */}
      <p class="qualifier">
        Incentive rates are set by NYSERDA, vary by utility territory, and are subject to
        available program funding. Your advisor will confirm what applies to your project.
      </p>

      <p class="provenance">
        Source: NYSERDA {vintage.label}. Designation is set by census tract, not by individual
        property.
      </p>

      <div class="actions actions-center">
        <ContactAdvisor stepName="Result" submissionId={state.submissionId} />
      </div>
    </section>
  );
}
